import React, { type FC } from 'react';
import { FormattedMessage, injectIntl, type WrappedComponentProps } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Lozenge from '@atlaskit/lozenge';
import SearchIcon from '@atlaskit/icon/core/search';
import AddIcon from '@atlaskit/icon/core/add';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';

import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import type { ContentState } from '../types.ts';

import type { DefaultSectionItem } from './ContentStateMenuV2.tsx';
import { i18n } from './contentStateMenuV2I18n.ts';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	row: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		justifyContent: 'space-between',
	},
	searchInput: {
		paddingInline: token('space.150'),
		paddingTop: token('space.100'),
	},
	after: {
		paddingInlineEnd: token('space.075'),
	},
	workflowStatusBadge: {
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		gap: token('space.100'),
		alignItems: 'center',
	},
	workflowStatusLabel: {
		alignItems: 'center',
		gap: token('space.100'),
	},
	changeStatusHeader: {
		paddingInline: token('space.150'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	changeStatusHeaderLeft: {
		alignItems: 'center',
		gap: token('space.050'),
	},
	badgeWrapperStyles: {
		backgroundColor: token('elevation.surface.sunken'),
		padding: token('space.025'),
	},
	iconStyles: {
		marginLeft: token('space.negative.025'),
	},
});

type StatusOptionRowProps = {
	option: ContentState;
	onSelect: (status: ContentState) => void;
};

const StatusOptionRow: FC<StatusOptionRowProps> = ({ option, onSelect }) => {
	const handleClick = () => {
		if (option && option.name) {
			onSelect(option);
		}
	};

	return (
		<ButtonItem
			iconBefore={
				<ContentStateColor
					color={option.color}
					restrictionLevel={option.restrictionLevel}
					appearance="contentStateButton"
				/>
			}
			onClick={handleClick}
		>
			{option.name}
		</ButtonItem>
	);
};

type WorkflowStatusOptionRowProps = {
	status: WorkflowStatus;
	onSelect: (status: WorkflowStatus) => void;
};

const WorkflowStatusOptionRow: FC<WorkflowStatusOptionRowProps> = ({ status, onSelect }) => {
	const handleClick = () => onSelect(status);

	return (
		<ButtonItem
			iconBefore={
				<Box xcss={styles.iconStyles}>
					<StatusWorkflowInReviewIcon
						label=""
						size="small"
						color={token('color.icon.accent.blue')}
						spacing="none"
					/>
				</Box>
			}
			onClick={handleClick}
			testId={`workflow-status-option-${status.id}`}
		>
			<Inline xcss={styles.row}>
				<Inline xcss={styles.workflowStatusLabel}>
					{status.name}
					{status.allowsReviews && (
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.approvalBetaTag} />
						</Lozenge>
					)}
				</Inline>
				<Inline xcss={styles.workflowStatusBadge}>
					<Box xcss={styles.badgeWrapperStyles}>
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...i18n.startApproval} />
						</Text>
					</Box>

					<ChevronRightIcon size="small" label="" />
				</Inline>
			</Inline>
		</ButtonItem>
	);
};

type ContentStateMenuV2SelectViewProps = WrappedComponentProps & {
	searchValue: string;
	onSearchChange: (value: string) => void;
	filteredDefaultSection: DefaultSectionItem[];
	filteredCustomOptions: ContentState[];
	hasResults: boolean;
	hasCurrentStatus: boolean;
	onSelectStatus: (status: ContentState) => void;
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
	onAddCustomClick: () => void;
	onRemove: () => void;
	onBack: () => void;
	onInvokeRovo: (() => void) | null;
};

const ContentStateMenuV2SelectViewComponent: FC<ContentStateMenuV2SelectViewProps> = ({
	searchValue,
	onSearchChange,
	filteredDefaultSection,
	filteredCustomOptions,
	hasResults,
	hasCurrentStatus,
	onSelectStatus,
	onSelectWorkflowStatus,
	onAddCustomClick,
	onRemove,
	onBack,
	onInvokeRovo,
	intl: { formatMessage },
}) => {
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onSearchChange(e.currentTarget.value);

	return (
		<Stack xcss={styles.dialog} testId="status-selector-popup-menu">
			{hasCurrentStatus && (
				<Inline xcss={styles.changeStatusHeader} testId="change-status-header">
					<Inline xcss={styles.changeStatusHeaderLeft} space="space.100">
						<IconButton
							icon={ArrowLeftIcon}
							label="Back"
							spacing="compact"
							onClick={onBack}
							testId="change-status-back-button"
						/>
						<Heading as="h2" size="xsmall">
							{formatMessage(i18n.changeStatus)}
						</Heading>
					</Inline>
					<Inline space="space.100" alignInline="end">
						{onInvokeRovo && (
							<Button
								onClick={onInvokeRovo}
								spacing="compact"
								iconBefore={RovoIcon}
								testId="invoke-rovo-button"
							>
								{formatMessage(i18n.rovo)}
							</Button>
						)}
						<Button onClick={onRemove} spacing="compact" testId="clear-status-button">
							{formatMessage(i18n.clear)}
						</Button>
					</Inline>
				</Inline>
			)}
			<MenuGroup maxHeight={360}>
				<Section>
					<Inline xcss={styles.searchInput}>
						<Textfield
							name="status-search"
							aria-label={formatMessage(i18n.searchPlaceholder)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							maxLength={20}
							isCompact
							value={searchValue}
							onChange={handleSearchChange}
							elemAfterInput={
								<Box xcss={styles.after}>
									<SearchIcon label="" />
								</Box>
							}
							testId="status-search-input"
						/>
					</Inline>
				</Section>
				<Section isScrollable>
					{filteredDefaultSection.length > 0 && (
						<Section title={formatMessage(i18n.defaultWorkflow)}>
							<MenuGroup>
								{filteredDefaultSection.map((item) =>
									item.type === 'contentState' ? (
										<StatusOptionRow
											key={item.contentState.id}
											option={item.contentState}
											onSelect={onSelectStatus}
										/>
									) : (
										<WorkflowStatusOptionRow
											key={`workflow-${item.workflowStatus.id}`}
											status={item.workflowStatus}
											onSelect={onSelectWorkflowStatus}
										/>
									),
								)}
							</MenuGroup>
						</Section>
					)}
					{filteredCustomOptions.length > 0 && (
						<Section title={formatMessage(i18n.customStatuses)}>
							{filteredCustomOptions.map((option) => (
								<StatusOptionRow key={option.id} option={option} onSelect={onSelectStatus} />
							))}
						</Section>
					)}
					{!hasResults && searchValue.trim() && (
						<Section>
							<ButtonItem
								iconBefore={<AddIcon label="" />}
								description={formatMessage(i18n.addCustomStatusDescription)}
								onClick={onAddCustomClick}
								testId="add-custom-status-button"
							>
								{formatMessage(i18n.addCustomStatus)}
							</ButtonItem>
						</Section>
					)}
				</Section>
			</MenuGroup>
		</Stack>
	);
};

export const ContentStateMenuV2SelectView = injectIntl(ContentStateMenuV2SelectViewComponent);
